import {
  LEGAL_SUFFIXES,
  SEGMENT_SEPARATORS,
  COMPANY_STOP_WORDS as STOP_WORDS,
} from './index';

// Cap so a large team can't fan out into dozens of search round-trips on mount.
export const MAX_EMAIL_QUERIES = 8;
// Member names are only fed in for *small* teams (see caller): a solo agent is
// billed under their own name, but a big brokerage should match on its brand.
export const MAX_MEMBER_NAME_QUERIES = 5;

/**
 * Queries sent to Stripe / Close for auto-matching: the org/member name(s), the
 * website, and the org + team emails (so the backend can find a billing account
 * by its exact Stripe email even when the org name never surfaces it). Name
 * splitting/variants happen on the backend (`buildSearchVariants`); duplicating
 * them here would only multiply round-trips.
 */
export const buildSuggestedQueries = (
  names: string[],
  website?: string | null,
  emails?: ReadonlyArray<string | null | undefined>,
): string[] => {
  const queries = names
    .map(name => name.trim())
    .filter(name => name.length >= 2);
  if (website != null && website.trim().length > 3) {
    queries.push(website.trim());
  }
  const seenEmails = new Set<string>();
  for (const email of emails ?? []) {
    const trimmed = email?.trim();
    if (trimmed == null || !isEmailLike(trimmed)) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seenEmails.has(key)) {
      continue;
    }
    seenEmails.add(key);
    queries.push(trimmed);
    if (seenEmails.size >= MAX_EMAIL_QUERIES) {
      break;
    }
  }
  return Array.from(new Set(queries));
};

// ---------------------------------------------------------------------------
// Match scoring. Once Stripe/Close return candidates we weight name tokens by
// how rare they are in the result set (IDF): a franchise brand shared by every
// candidate ("iad") collapses toward 0 while a person's surname keeps weight.
// ---------------------------------------------------------------------------

/** Normalise a string into discriminative lowercase ASCII tokens (≥2 chars). */
export const tokenize = (value: string): string[] =>
  value
    .replace(LEGAL_SUFFIXES, ' ')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^a-z0-9]+/)
    .map(t => t.trim())
    .filter(t => t.length >= 2 && !STOP_WORDS.has(t));

export type ScorableMatch = { name?: string | null; email?: string | null };
export type MatchEval = { score: number; recommended: boolean };
export type EvaluatedMatch<T extends ScorableMatch> = MatchEval & { match: T };

/** A candidate covering ≥ this share of a query's weight is "recommended". */
export const RECOMMEND_THRESHOLD = 0.6;

// Two tokens match when equal or when one is a near-complete prefix of the other
// (≥5 chars, within 2 chars of the longer) — covers "christoph"/"christophe"
// without matching merely-shared stems like "maison"/"maisonette".
const tokenMatches = (queryToken: string, nameTokens: string[]): boolean =>
  nameTokens.some(nt => {
    if (nt === queryToken) {
      return true;
    }
    const [short, long] =
      nt.length <= queryToken.length ? [nt, queryToken] : [queryToken, nt];
    return (
      short.length >= 5 &&
      long.startsWith(short) &&
      long.length - short.length <= 2
    );
  });

/** A website/URL is a retrieval hint, not a name — dropped from scoring. */
export const isUrlLike = (value: string): boolean =>
  /:\/\//.test(value) || /^[^\s@]+\.[^\s]{2,}$/.test(value.trim());

/** An email is a retrieval + exact-match hint, but must not pollute name scoring. */
export const isEmailLike = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

/** Normalise an email for set membership (trimmed, lower-cased) or null. */
export const normalizeEmail = (email?: string | null): string | null => {
  const trimmed = email?.trim().toLowerCase();
  return trimmed != null && trimmed !== '' ? trimmed : null;
};

/** `normalizeEmail` over a list, dropping the empty ones. */
export const normalizeEmails = (
  emails: ReadonlyArray<string | null | undefined>,
): string[] =>
  emails
    .map(email => normalizeEmail(email))
    .filter((email): email is string => email != null);

/**
 * Tokens of a website's *registrable* domain (the brand/platform, not the
 * person). `smitteault.revo.immo` → `["revo", "immo"]` (the per-agent subdomain
 * is dropped); `naef.ch` → `["naef", "ch"]` (no subdomain, so the brand is the
 * identity).
 */
export const extractBrandTokens = (value: string): string[] => {
  let host = value.trim().toLowerCase();
  host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  host = host.split(/[/?#]/)[0] ?? host;
  host = host.split('@').pop() ?? host;
  host = host.split(':')[0] ?? host;
  let labels = host.split('.').filter(Boolean);
  if (labels[0] === 'www') {
    labels = labels.slice(1);
  }
  if (labels.length >= 3) {
    labels = labels.slice(1);
  }
  return labels.filter(label => label.length >= 2);
};

/**
 * Expand retrieval queries into the strings used purely for *scoring* — each
 * name plus its entity segments ("A | B" → "A", "B"), URLs/emails dropped. Local
 * and free; lets a candidate matching only the company part still be scored.
 */
export const buildScoringQueries = (queries: string[]): string[] => {
  const out: string[] = [];
  for (const query of queries) {
    if (isUrlLike(query) || isEmailLike(query)) {
      continue;
    }
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      continue;
    }
    out.push(trimmed);
    const segments = trimmed
      .split(SEGMENT_SEPARATORS)
      .map(s => s.trim())
      .filter(s => s.length >= 2);
    if (segments.length > 1) {
      out.push(...segments);
    }
  }
  return Array.from(new Set(out));
};

export type ScoringContext = {
  scoringQueries: string[];
  /** Domain/platform tokens whose scoring weight is forced to 0. */
  brandTokens: Set<string>;
};

/**
 * Build scoring queries plus the *brand* tokens to neutralise. For a person on a
 * platform — "Sylvie Mitteault - Revo Immo" on `smitteault.revo.immo` — the
 * platform brand ("revo", "immo") recurs across many agents and can outrank the
 * surname, so a brand-only candidate wrongly clears the threshold. We zero the
 * registrable-domain tokens, but only when the name carries identity beyond the
 * domain (a plain "Naef"/`naef.ch` keeps its token discriminative).
 */
export const buildScoringContext = (queries: string[]): ScoringContext => {
  const scoringQueries = buildScoringQueries(queries);

  const domainTokens = new Set<string>();
  for (const query of queries) {
    if (isUrlLike(query)) {
      for (const token of extractBrandTokens(query)) {
        domainTokens.add(token);
      }
    }
  }

  const nameTokens = new Set<string>();
  for (const query of scoringQueries) {
    for (const token of tokenize(query)) {
      nameTokens.add(token);
    }
  }

  const hasIdentityBeyondDomain = [...nameTokens].some(
    token => !domainTokens.has(token),
  );
  const brandTokens = hasIdentityBeyondDomain
    ? domainTokens
    : new Set<string>();

  return { scoringQueries, brandTokens };
};

/**
 * Inverse document frequency of every token across the candidate set. The email
 * is folded into each candidate's document so a brand that recurs across emails
 * (dozens of `@tecnocasa.it`) collapses toward 0 even when names use distinct
 * legal entities. Matching still runs on names only (below).
 */
export const buildIdf = (matches: ScorableMatch[]): Map<string, number> => {
  const df = new Map<string, number>();
  for (const match of matches) {
    const tokens = new Set([
      ...tokenize(match.name ?? ''),
      ...tokenize(match.email ?? ''),
    ]);
    for (const token of tokens) {
      df.set(token, (df.get(token) ?? 0) + 1);
    }
  }
  const n = matches.length;
  const idf = new Map<string, number>();
  for (const [token, freq] of df) {
    idf.set(token, Math.log((n + 1) / (freq + 1)));
  }
  return idf;
};

// A query token absent from the corpus can't discriminate, so it weighs 0.
const idfOf = (token: string, idf: Map<string, number>): number =>
  idf.get(token) ?? 0;

const EMPTY_BRAND_TOKENS: Set<string> = new Set();
const EMPTY_EMAILS: ReadonlySet<string> = new Set();

/**
 * Evaluate a candidate against the queries with corpus-aware token weights.
 * Returns a [0,1] `score` (for sorting) and a `recommended` flag.
 *
 * - An exact email match (candidate email ∈ org/team contacts) is recommended
 *   outright — the billing account provably belongs to that person.
 * - Otherwise `score` is the best IDF-weighted recall of a query's
 *   discriminative tokens; a brand-only candidate scores ~0.
 */
export const evalMatch = (
  match: ScorableMatch,
  queries: string[],
  idf: Map<string, number>,
  brandTokens: Set<string> = EMPTY_BRAND_TOKENS,
  knownEmails: ReadonlySet<string> = EMPTY_EMAILS,
): MatchEval => {
  const email = normalizeEmail(match.email);
  if (email != null && knownEmails.has(email)) {
    return { score: 1, recommended: true };
  }

  const nameTokens = tokenize(match.name ?? '');
  if (nameTokens.length === 0) {
    return { score: 0, recommended: false };
  }
  // A candidate that is *only* brand/platform tokens (a bare "Efficity" HQ
  // account for an org on `*.efficity.com`) carries no identity — never
  // recommend it. Brand-is-identity orgs have an empty brandTokens set.
  const isBrandOnly =
    brandTokens.size > 0 && nameTokens.every(nt => brandTokens.has(nt));
  let score = 0;
  let recommended = false;
  for (const query of queries) {
    const queryTokens = tokenize(query);
    if (queryTokens.length === 0) {
      continue;
    }
    // Require covering ≥ half a query's tokens so a single coincidental common
    // word ("futura") can't recommend an unrelated office.
    const minMatched = Math.ceil(queryTokens.length / 2);
    let totalWeight = 0;
    let matchedWeight = 0;
    let matchedCount = 0;
    for (const qt of queryTokens) {
      const weight = brandTokens.has(qt) ? 0 : idfOf(qt, idf);
      totalWeight += weight;
      if (tokenMatches(qt, nameTokens)) {
        matchedWeight += weight;
        matchedCount += 1;
      }
    }
    if (totalWeight > 0) {
      const recall = matchedWeight / totalWeight;
      score = Math.max(score, recall);
      if (recall >= RECOMMEND_THRESHOLD && matchedCount >= minMatched) {
        recommended = true;
      }
    } else {
      // Every query token is ubiquitous (idf 0, e.g. a bare "IAD" query): count
      // a match only when the candidate adds no discriminative signal of its own
      // — duplicate "Acme" accounts match, "IAD - Goncalves" does not.
      const hasUnexplainedSignal = nameTokens.some(
        nt =>
          idfOf(nt, idf) > 0 &&
          !brandTokens.has(nt) &&
          !tokenMatches(nt, queryTokens),
      );
      if (!hasUnexplainedSignal) {
        const recall = matchedCount / queryTokens.length;
        score = Math.max(score, recall);
        if (recall >= RECOMMEND_THRESHOLD && matchedCount >= minMatched) {
          recommended = true;
        }
      }
    }
  }
  return {
    score: isBrandOnly ? 0 : score,
    recommended: recommended && !isBrandOnly,
  };
};

/**
 * Email is the authority signal across a scored candidate set:
 *
 * - When any candidate's email is one of the org/team contacts, that's ground
 *   truth — recommend only the candidates sharing a known email and drop every
 *   name-only recommendation (the "every other Rudy" noise a franchise
 *   first-name query drags in).
 * - Otherwise, promote duplicate billing accounts of the same person: once any
 *   candidate is recommended by name, recommend the rest sharing its email
 *   ("Thomas Richard-Mas" + "EIRL Richard-Mas Thomas", same gmail).
 */
export const applyEmailAuthority = <T extends ScorableMatch>(
  evaluated: Array<EvaluatedMatch<T>>,
  knownEmails: ReadonlySet<string>,
): Array<EvaluatedMatch<T>> => {
  const emailOf = (match: T) => normalizeEmail(match.email);
  const isKnown = (match: T) => {
    const email = emailOf(match);
    return email != null && knownEmails.has(email);
  };

  if (knownEmails.size > 0 && evaluated.some(entry => isKnown(entry.match))) {
    return evaluated.map(entry =>
      isKnown(entry.match)
        ? { ...entry, recommended: true, score: Math.max(entry.score, 1) }
        : { ...entry, recommended: false },
    );
  }

  const anchorEmails = new Set(
    evaluated.flatMap(entry => {
      const email = emailOf(entry.match);
      return entry.recommended && email != null ? [email] : [];
    }),
  );
  if (anchorEmails.size === 0) {
    return evaluated;
  }
  return evaluated.map(entry => {
    if (entry.recommended) {
      return entry;
    }
    const email = emailOf(entry.match);
    return email != null && anchorEmails.has(email)
      ? { ...entry, recommended: true, score: Math.max(entry.score, 1) }
      : entry;
  });
};

/**
 * Score a candidate set against the queries with corpus-aware weights. One
 * place builds the IDF, evaluates and applies the email authority, so retrieval
 * sorting and recommendation always agree on a candidate's standing.
 */
export const evaluateMatches = <T extends ScorableMatch>(
  list: T[],
  scoringQueries: string[],
  brandTokens: Set<string>,
  knownEmails: ReadonlySet<string>,
): Array<EvaluatedMatch<T>> => {
  const idf = buildIdf(list);
  return applyEmailAuthority(
    list.map(match => ({
      match,
      ...evalMatch(match, scoringQueries, idf, brandTokens, knownEmails),
    })),
    knownEmails,
  );
};

// Candidates kept after scoring: the best N by score are shown, and the
// recommended ones are picked among those.
export const SHOWN_MATCH_LIMIT = 8;

/** Best-scoring candidates first, capped at `limit`. */
export const topScoredMatches = <T extends ScorableMatch>(
  evaluated: Array<EvaluatedMatch<T>>,
  limit: number = SHOWN_MATCH_LIMIT,
): T[] =>
  [...evaluated]
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(entry => entry.match);

/** Only the candidates flagged `recommended`, in their current order. */
export const recommendedMatches = <T extends ScorableMatch>(
  evaluated: Array<EvaluatedMatch<T>>,
): T[] =>
  evaluated.filter(entry => entry.recommended).map(entry => entry.match);
